/**
 * Prediction-interval helpers: quantiles, alignment of backend bands to the
 * result dates, and coverage scoring. Pure and unit-tested.
 */

/** Linear-interpolated sample quantile (p in [0,1]); null for empty input. */
export function quantile(values, p) {
  const xs = (values || []).map(Number).filter(Number.isFinite).sort((a, b) => a - b);
  if (!xs.length) return null;
  if (xs.length === 1) return xs[0];
  const pos = Math.min(Math.max(p, 0), 1) * (xs.length - 1);
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  return xs[lo] + (xs[hi] - xs[lo]) * (pos - lo);
}

/**
 * Empirical bands from per-horizon error samples.
 * `errorsByHorizon` = {h: [f − a, …]}; returns {h: {lower, upper}} offsets such
 * that the interval around a forecast f is [f − upper, f − lower]… expressed
 * directly as additive offsets `{lo, hi}` to apply to the forecast.
 * Requires at least `minN` errors per horizon; otherwise that horizon is null.
 */
export function empiricalOffsets(errorsByHorizon, level, minN = 20) {
  const alpha = (1 - level / 100) / 2;
  const out = {};
  for (const h of Object.keys(errorsByHorizon || {})) {
    const e = (errorsByHorizon[h] || []).map(Number).filter(Number.isFinite);
    if (e.length < minN) {
      out[h] = null;
      continue;
    }
    // forecast error e = f − a ⇒ a = f − e ⇒ interval for a is [f − q(1−α), f − q(α)]
    out[h] = { lo: -quantile(e, 1 - alpha), hi: -quantile(e, alpha), n: e.length };
  }
  return out;
}

/**
 * Pick the interval documents of one series/scenario from a v2 document and
 * align them to `dates` (the result's dates). Returns
 * `{level: {lower: [...], upper: [...]}}` with null where a date is missing.
 */
export function alignBands(doc, series, scenario, dates) {
  if (!doc || !Array.isArray(doc.intervals) || !Array.isArray(doc.dates)) return {};
  const idx = new Map(doc.dates.map((d, i) => [d, i]));
  const out = {};
  for (const band of doc.intervals) {
    if (band.series !== series || band.scenario !== scenario) continue;
    const at = (arr, d) => {
      const i = idx.get(d);
      return i == null || arr[i] == null ? null : Number(arr[i]);
    };
    out[band.level] = {
      lower: dates.map((d) => at(band.lower, d)),
      upper: dates.map((d) => at(band.upper, d)),
      method: band.method,
      sample: band.sample || null,
      pointwise: band.pointwise !== false,
    };
  }
  return out;
}

/** `{lower:[], upper:[]}` aligned to dates → `[[date, v], …]` pairs without nulls. */
export function bandPairs(dates, band) {
  if (!band) return { lower: [], upper: [] };
  const pick = (arr) => dates.map((d, i) => [d, arr[i]]).filter((p) => p[1] != null);
  return { lower: pick(band.lower), upper: pick(band.upper) };
}

/**
 * Sanity check: do the bands widen with horizon? Returns the share (0–1) of
 * consecutive steps where width does not shrink, and the first index where it
 * does, so the UI can state non-monotonic behaviour instead of hiding it.
 */
export function widthMonotonicity(band) {
  if (!band || !band.lower) return null;
  const widths = band.lower.map((lo, i) => (lo == null || band.upper[i] == null ? null : band.upper[i] - lo)).filter((w) => w != null);
  if (widths.length < 2) return null;
  let ok = 0;
  let firstShrink = -1;
  for (let i = 1; i < widths.length; i++) {
    if (widths[i] >= widths[i - 1] - 1e-12) ok++;
    else if (firstShrink < 0) firstShrink = i;
  }
  return { share: ok / (widths.length - 1), firstShrink };
}

/** Human label for a band's method and sample. */
export function methodLabel(level, band) {
  if (!band) return "";
  const names = { empirical: "empirical OOS errors", simulation: "residual-bootstrap simulation", illustrative: "illustrative (fixture)" };
  let s = `${level}% prediction interval · ${names[band.method] || band.method || "unknown method"}`;
  if (band.sample && band.sample.from) s += ` · ${band.sample.from}–${band.sample.to}`;
  if (band.sample && band.sample.vintages) s += ` · ${band.sample.vintages} vintages`;
  if (band.pointwise) s += " · pointwise";
  return s;
}
